import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaChartLine,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaCloudUploadAlt,
  FaReceipt,
  FaRupeeSign,
  FaShoppingBag,
  FaSyncAlt,
  FaUtensils,
} from "react-icons/fa";
import { SESSION_UPDATED_EVENT } from "../utils/accountLinks";
import {
  clearImpersonationStorage,
  isImpersonating,
  isMasterSession,
  setStoredSession,
} from "../utils/impersonation";
import { resetLogRocketSession } from "../utils/logRocket";
import { formatStoredMoney, resolveCurrencyContext } from "../utils/currency";
import { API_ENDPOINTS } from "../config/constants";
import { initializeApp, getApps } from "firebase/app";
import { getDatabase, onValue, ref } from "firebase/database";

/**
 * BillSarthi Web Dashboard — aligned with mobile dashboard
 * - Uses /getorderlist with today's date (fromdate/todate)
 * - Shows same core fields: ordercode, totalamount, created_at, tableid, added_by_name
 * - Offline sync using localStorage("createorderitem")
 */

const BRAND = "#015b9a";
const DASHBOARD_INSIGHTS_OPEN_KEY = "dashboard_owner_insights_open_v1";
const firebaseConfig = {
  apiKey: "AIzaSyDbfZS3ZazugHXoSIvBk--P6coVstYX7R8",
  projectId: "estro-mobile-app",
  storageBucket: "restro-mobile-app.firebasestorage.app",
  messagingSenderId: "7236168554",
  appId: "1:7236168554:ios:d5114808c53aadb4671313",
  databaseURL:
    "https://restro-mobile-app-default-rtdb.asia-southeast1.firebasedatabase.app",
};

const getFirebaseDatabase = () => {
  if (getApps().length === 0) initializeApp(firebaseConfig);
  return getDatabase();
};

const firebaseTableIsActive = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  if (Array.isArray(entry?.line_items)) {
    return entry.line_items.some(
      (line) => Number(line?.quantity ?? line?.qty ?? 0) > 0
    );
  }
  if (entry?.products && typeof entry.products === "object") {
    return Object.values(entry.products).some((qty) => Number(qty) > 0);
  }
  return Boolean(entry?.hasdata);
};

const Dashboard = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [offlineCount, setOfflineCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [tableMetaMap, setTableMetaMap] = useState({});
  const [tableList, setTableList] = useState([]);
  const [liveTableMap, setLiveTableMap] = useState(null);
  const [insightsOpen, setInsightsOpen] = useState(() => {
    try {
      return localStorage.getItem(DASHBOARD_INSIGHTS_OPEN_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    const pad = (v) => String(v).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });

  useEffect(() => {
    if (!userData) return;
    const resolvedRegion = resolveCurrencyContext(userData);
    console.log("[Dashboard] Region settings", {
      resolved: resolvedRegion,
      profile: {
        country_code: userData?.country_code ?? userData?.countryCode,
        currency_code: userData?.currency_code ?? userData?.currencyCode,
        locale: userData?.locale,
        timezone: userData?.timezone,
      },
      usingIndiaFallback:
        !userData?.currency_code &&
        !userData?.currencyCode &&
        resolvedRegion.currencyCode === "INR",
    });
  }, [userData]);

  /* ------------------------------ helpers ------------------------------ */
  const formatINR = formatStoredMoney;

  const parseAnyDate = (d) => {
    if (!d) return new Date(0);
    const isoish = typeof d === "string" ? d.replace(" ", "T") : d;
    const dt = new Date(isoish);
    return isNaN(dt.getTime()) ? new Date(0) : dt;
  };

  const pad2 = (v) => String(v).padStart(2, "0");

  const formatDate = (date) => {
    const d = parseAnyDate(date);
    return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
  };

  /* ---------- table helpers (parity with mobile dashboard) ---------- */
  const isTakeawayName = (name) =>
    typeof name === "string" && name.toLowerCase().startsWith("takeaway");

  const extractTakeawayIndex = (name) => {
    if (!name) return null;
    const lower = String(name).toLowerCase();
    if (lower === "takeaway") return 1;
    const match = lower.match(/takeaway[\\s-]*(\\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      return Number.isNaN(num) ? null : num;
    }
    return null;
  };

  const formatTakeawayLabel = (name) => {
    const raw =
      name === null || name === undefined ? "" : String(name).trim();

    // Dashboard API sometimes sends table id/name as 0 or -1 for takeaways
    if (!raw || raw === "0" || raw === "-1") return "TakeAway";

    const idx = extractTakeawayIndex(raw);
    return idx ? `TakeAway ${idx}` : raw;
  };

  const getTableKey = (table) => {
    if (!table) return "";
    const candidate =
      table?.table_key ??
      table?.tableKey ??
      table?.key ??
      table?.name ??
      table?.table ??
      table?.tableid ??
      table?.tableId ??
      table?.id ??
      "";
    return candidate === null || candidate === undefined ? "" : String(candidate);
  };

  const getTableDisplayName = (table) => {
    if (!table) return "";
    const candidate =
      table?.display_name ??
      table?.displayName ??
      table?.table_key ??
      table?.tableKey ??
      table?.name ??
      table?.table ??
      table?.tableid ??
      table?.tableId ??
      table?.id ??
      "";
    return candidate === null || candidate === undefined ? "" : String(candidate);
  };

  const getTableType = (table) => {
    const raw =
      table?.table_type ?? table?.tableType ?? table?.tabletype ?? table?.type;
    return String(raw || "").toLowerCase() === "room" ? "room" : "table";
  };

  const buildTableMetaMap = (list = []) => {
    const map = {};
    if (!Array.isArray(list)) return map;
    list.forEach((entry) => {
      const key = getTableKey(entry);
      const numericName = entry?.name;
      const displayName = getTableDisplayName(entry);
      const type = getTableType(entry);
      const resolvedDisplay =
        displayName ||
        (key ? String(key) : numericName !== undefined ? String(numericName) : "");
      const meta = {
        displayName: resolvedDisplay,
        type,
      };
      const addKey = (k) => {
        if (k === null || k === undefined || k === "") return;
        map[String(k)] = meta;
      };
      addKey(key);
      addKey(numericName);
      addKey(entry?.id);
      addKey(entry?.tableid);
      addKey(entry?.tableId);
      addKey(entry?.table_id);
    });
    return map;
  };

  const resolveTableLabel = (order) => {
    const rawKey =
      order?.tableid ??
      order?.table ??
      order?.table_no ??
      order?.tableid ??
      order?.tablename ??
      order?.table_name ??
      "";
    const key = rawKey === null || rawKey === undefined ? "" : String(rawKey);
    const isTakeaway =
      key === "" ||
      key === "0" ||
      key === "-1" ||
      Number(key) === 0 ||
      Number(key) === -1 ||
      isTakeawayName(key);

    if (isTakeaway) {
      return { label: formatTakeawayLabel(key || "TakeAway-1"), type: "takeaway" };
    }

    const meta =
      tableMetaMap[key] ||
      tableMetaMap[String(order?.tableid)] ||
      tableMetaMap[String(order?.table)] ||
      {};
    const typeRaw =
      (
        order?.table_type ||
        order?.tableType ||
        order?.tabletype ||
        ""
      )
        .toString()
        .toLowerCase();

    // Heuristics to match mobile: prefer meta; fall back to order fields containing "room"
    const displayCandidate =
      meta?.displayName ||
      order?.table_label ||
      order?.tablename ||
      order?.table_name ||
      order?.table ||
      key ||
      "-";
    const displayLower =
      typeof displayCandidate === "string"
        ? displayCandidate.toLowerCase()
        : "";

    const looksRoom =
      typeRaw === "room" ||
      (displayLower && /ro+om/.test(displayLower));

    const type = meta?.type || (looksRoom ? "room" : "table");

    const normalizeLabel = (val = "") => {
      if (typeof val !== "string") return String(val || "");
      if (!val.trim()) return "-";
      const first = val.trim();
      return first.charAt(0).toUpperCase() + first.slice(1);
    };

    let label;
    if (type === "room") {
      if (displayLower.startsWith("room")) {
        label = normalizeLabel(displayCandidate);
      } else {
        label = `Room ${normalizeLabel(displayCandidate)}`;
      }
    } else {
      label = `Table ${normalizeLabel(displayCandidate)}`;
    }

    const displayName =
      typeof displayCandidate === "string" && displayCandidate.trim()
        ? displayCandidate
        : key || "-";

    return { label, type, displayName };
  };

  const getRelativeTime = (date) => {
    const now = Date.now();
    const t = parseAnyDate(date).getTime();
    const diffMs = Math.max(0, now - t);
    const mins = Math.floor(diffMs / (1000 * 60));
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  };

  const fetchOrders = useCallback(
    async (restaurantid, opts = {}) => {
      try {
        setLoading(true);

        const body = {
          restaurantid,
          fromdate: selectedDate,
          todate: selectedDate,
        };

        const response = await fetch(
          API_ENDPOINTS.GET_ORDER_LIST,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || "Failed to load orders");
        }

        const list = Array.isArray(result?.list) ? result.list : [];
        const sorted = [...list].sort(
          (a, b) =>
            parseAnyDate(b?.created_at).getTime() -
            parseAnyDate(a?.created_at).getTime()
        );

        setOrders(sorted);
        setStatus("");
      } catch (err) {
        console.error("Order fetch failed", err);
        toast(err?.message || "Failed to load orders", "error");
        setStatus("Offline mode — showing last loaded results (if any).");
      } finally {
        setLoading(false);
      }
    },
    [selectedDate]
  );

  const loadTableMeta = useCallback(
    async (restaurantid) => {
      // try cache first
      try {
        const cachedRaw = localStorage.getItem("tablelistx");
        if (cachedRaw) {
          const cachedList = JSON.parse(cachedRaw);
          if (Array.isArray(cachedList)) {
            setTableMetaMap(buildTableMetaMap(cachedList));
            setTableList(cachedList);
          }
        }
      } catch {}

      if (!restaurantid) return;
      try {
        const response = await fetch(
          API_ENDPOINTS.LIST_TABLE,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid }),
          }
        );
        const result = await response.json();
        if (response.ok && Array.isArray(result?.list)) {
          const map = buildTableMetaMap(result.list);
          setTableMetaMap(map);
          setTableList(result.list);
          localStorage.setItem("tablelistx", JSON.stringify(result.list));
        }
      } catch (e) {
        console.log("table list load failed on dashboard:", e?.message || e);
      }
    },
    []
  );

  const checkOfflineOrders = useCallback(() => {
    const stored = localStorage.getItem("createorderitem");
    if (stored) {
      try {
        const list = JSON.parse(stored);
        setOfflineCount(Array.isArray(list) ? list.length : 0);
      } catch {
        setOfflineCount(0);
      }
    } else {
      setOfflineCount(0);
    }
  }, []);

  const readStoredDashboardUser = useCallback(() => {
    const stored = localStorage.getItem("userData");
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored);
      return parsed?.data || parsed || null;
    } catch {
      return null;
    }
  }, []);

  const syncDashboardUserFromStorage = useCallback(() => {
    const nextUser = readStoredDashboardUser();
    setUserData(nextUser);
    checkOfflineOrders();
    return nextUser;
  }, [checkOfflineOrders, readStoredDashboardUser]);

  const forceLogout = useCallback(() => {
    clearImpersonationStorage();
    localStorage.removeItem("webLoginCredentials");
    resetLogRocketSession();
    localStorage.removeItem("userData");
    setUserData(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  /* ------------------------------ effects ------------------------------ */
  useEffect(() => {
    const nextUser = syncDashboardUserFromStorage();
    if (!nextUser) {
      navigate("/login");
      return;
    }
  }, [navigate, syncDashboardUserFromStorage]);

  useEffect(() => {
    const handleSessionUpdated = () => {
      const nextUser = syncDashboardUserFromStorage();
      if (!nextUser) {
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
    window.addEventListener("storage", handleSessionUpdated);
    return () => {
      window.removeEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
      window.removeEventListener("storage", handleSessionUpdated);
    };
  }, [navigate, syncDashboardUserFromStorage]);

  useEffect(() => {
    if (!userData?.id) return;
    if (isImpersonating() || isMasterSession(userData)) return;

    let session = {};
    try {
      session = JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      session = {};
    }

    const storedUser = session?.data || session || {};
    const mobile_number = String(
      storedUser?.mobile_number || storedUser?.phone || ""
    ).trim();
    const password = String(storedUser?.password || "");

    if (!mobile_number || !password) return;

    let cancelled = false;

    const refreshWebAccess = async () => {
      try {
        const response = await fetch(
          API_ENDPOINTS.LOGIN,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mobile_number,
              password,
            }),
          }
        );

        const result = await response.json().catch(() => ({}));
        if (!response.ok) return;

        const rawIsWebAllowed =
          result?.data?.isWebAllowed ??
          result?.data?.isweballowed ??
          result?.isWebAllowed ??
          result?.isweballowed;

        if (
          rawIsWebAllowed === 0 ||
          rawIsWebAllowed === "0" ||
          rawIsWebAllowed === false
        ) {
          if (!cancelled) forceLogout();
          return;
        }

        if (!cancelled && result?.data) {
          setStoredSession(result);
          setUserData(result.data);
        }
      } catch (error) {
        console.log(
          "dashboard login/restro refresh skipped:",
          error?.message || error
        );
      }
    };

    refreshWebAccess();

    return () => {
      cancelled = true;
    };
  }, [forceLogout, userData?.id]);

  useEffect(() => {
    if (userData?.id) {
      fetchOrders(userData.id);
      loadTableMeta(userData.id);
    }
  }, [userData?.id, selectedDate, fetchOrders, loadTableMeta]);

  useEffect(() => {
    if (!userData?.id) return undefined;
    const tableRef = ref(
      getFirebaseDatabase(),
      `/restaurants/${userData.id}/tables`
    );
    return onValue(
      tableRef,
      (snapshot) => {
        const value = snapshot.exists() ? snapshot.val() : null;
        setLiveTableMap(value && typeof value === "object" ? value : null);
      },
      () => setLiveTableMap(null)
    );
  }, [userData?.id]);

  /* ------------------------------ actions ------------------------------ */
  const handleSync = async () => {
    try {
      const stored = localStorage.getItem("createorderitem");
      if (!stored) {
        toast("No pending offline orders", "info");
        return;
      }

      const queue = JSON.parse(stored);
      if (!Array.isArray(queue) || queue.length === 0) {
        toast("No pending offline orders", "info");
        return;
      }

      // match mobile: bulk endpoint
      const response = await fetch(
        API_ENDPOINTS.BULK_CREATE_ORDER,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orders: queue }),
        }
      );
      const json = await response.json();

      if (response.ok && json?.success) {
        toast(`Synced ${queue.length} orders`, "success");
        localStorage.removeItem("createorderitem");
        setOfflineCount(0);
        if (userData?.id) fetchOrders(userData.id, { refresh: true });
      } else {
        toast(json?.message || "No orders synced. Check connection.", "error");
      }
    } catch (err) {
      toast(err?.message || "Sync failed", "error");
    }
  };

  const handleRefresh = () => {
    const latestUser = readStoredDashboardUser();
    const latestRestaurantId = Number(latestUser?.id || 0);
    const currentRestaurantId = Number(userData?.id || 0);

    if (!latestRestaurantId && !currentRestaurantId) return;

    if (latestUser && latestRestaurantId !== currentRestaurantId) {
      setUserData(latestUser);
      checkOfflineOrders();
      return;
    }

    const restaurantId = latestRestaurantId || currentRestaurantId;
    fetchOrders(restaurantId, { refresh: true });
    loadTableMeta(restaurantId);
  };

  const deleteOrderFromDashboard = async (order) => {
    if (!order?.id || !userData?.id) return;
    setDeletingOrderId(order?.id ?? true);
    try {
      const response = await fetch(
        API_ENDPOINTS.DELETE_ORDER,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderid: order.id,
            restaurantid: userData?.id,
            deleted_by: userData?.id,
          }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast("Order deleted", "success");
        fetchOrders(userData.id, { refresh: true });
      } else {
        toast(result?.message || "Failed to delete order", "error");
      }
    } catch (error) {
      toast("Network error", "error");
    } finally {
      setDeletingOrderId(null);
    }
  };

  useEffect(() => {
    const onSync = () => handleSync();
    const onRefresh = () => handleRefresh();
    window.addEventListener("dashboard:sync", onSync);
    window.addEventListener("dashboard:refresh", onRefresh);
    return () => {
      window.removeEventListener("dashboard:sync", onSync);
      window.removeEventListener("dashboard:refresh", onRefresh);
    };
  }, [handleSync, handleRefresh]);

  /* ------------------------------ derived ------------------------------ */
  const totals = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    const count = list.length;
    const amount = list.reduce(
      (sum, o) => sum + Number(o?.totalamount || 0),
      0
    );
    const average = count ? amount / count : 0;
    return { count, amount, average };
  }, [orders]);

  const isToday = selectedDate === (() => {
    const date = new Date();
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  })();

  const insights = useMemo(() => {
    const hourly = Array.from({ length: 24 }, () => ({ count: 0, amount: 0 }));
    const staff = {};
    let dineIn = 0;
    let takeaway = 0;
    let recentOrders = 0;
    let recentRevenue = 0;
    let latestOrderAt = null;
    const now = Date.now();

    orders.forEach((order) => {
      const date = parseAnyDate(order?.created_at);
      if (date.getTime() > 0) {
        const hour = date.getHours();
        hourly[hour].count += 1;
        hourly[hour].amount += Number(order?.totalamount || 0);
        if (!latestOrderAt || date.getTime() > latestOrderAt.getTime()) {
          latestOrderAt = date;
        }
        if (isToday && now - date.getTime() >= 0 && now - date.getTime() <= 60 * 60 * 1000) {
          recentOrders += 1;
          recentRevenue += Number(order?.totalamount || 0);
        }
      }
      const table = resolveTableLabel(order);
      if (table.type === "takeaway") takeaway += 1;
      else dineIn += 1;

      const name = String(order?.added_by_name || "Owner").trim() || "Owner";
      if (!staff[name]) staff[name] = { name, orders: 0, amount: 0 };
      staff[name].orders += 1;
      staff[name].amount += Number(order?.totalamount || 0);
    });

    const activeHours = hourly
      .map((entry, hour) => ({ ...entry, hour }))
      .filter((entry) => entry.count > 0)
      .sort((a, b) => b.count - a.count || b.amount - a.amount);
    const peak = activeHours[0] || null;
    const topStaff = Object.values(staff).sort(
      (a, b) => b.amount - a.amount || b.orders - a.orders
    )[0];
    const maxHourly = Math.max(1, ...hourly.map((entry) => entry.amount));

    return {
      hourly,
      peak,
      topStaff,
      dineIn,
      takeaway,
      maxHourly,
      recentOrders,
      recentRevenue,
      latestOrderAt,
    };
  }, [orders, tableMetaMap, isToday]);

  const activeTableCount = useMemo(() => {
    return tableList.filter((table, index) => {
      const key = getTableKey(table) || String(index + 1);
      if (firebaseTableIsActive(liveTableMap?.[key])) return true;
      if (table?.hasdata) return true;
      try {
        const base = JSON.parse(localStorage.getItem(`ordersitems1${key}`) || "null");
        const lines = JSON.parse(localStorage.getItem(`ordersitems1_lines${key}`) || "null");
        const hasBase = base && typeof base === "object" && Object.keys(base).length > 0;
        const hasLines = Array.isArray(lines) && lines.length > 0;
        const hasAmount = Number(localStorage.getItem(`orderAmount1${key}`) || 0) > 0;
        return hasBase || hasLines || hasAmount;
      } catch {
        return false;
      }
    }).length;
  }, [tableList, liveTableMap]);

  const hourLabel = (hour) => {
    const start = new Date(2000, 0, 1, hour, 0);
    const end = new Date(2000, 0, 1, (hour + 1) % 24, 0);
    const fmt = (date) =>
      date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    return `${fmt(start)}–${fmt(end)}`;
  };

  const toggleInsights = () => {
    setInsightsOpen((current) => {
      const next = !current;
      try {
        localStorage.setItem(DASHBOARD_INSIGHTS_OPEN_KEY, String(next));
      } catch {
        // Keep the UI working even when browser storage is unavailable.
      }
      return next;
    });
  };

  /* --------------------------------- UI --------------------------------- */
  return (
    <div style={sx.page}>
      <section className="dashboard-owner-hero" style={sx.hero}>
        <div>
          <div style={sx.eyebrow}>OWNER OVERVIEW</div>
          <h1 style={sx.heroTitle}>
            {isToday ? "Here’s how today is going" : "Daily business snapshot"}
          </h1>
          <p style={sx.heroCopy}>
            {userData?.restaurant_name || "Your restaurant"} · Updated from live orders
          </p>
        </div>
        <div style={sx.heroActions}>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={sx.dateInput}
            aria-label="Select dashboard date"
          />
          <button style={sx.refreshButton} onClick={handleRefresh}>
            <FaSyncAlt size={12} /> Refresh
          </button>
        </div>
      </section>

      {offlineCount > 0 ? (
        <button style={sx.syncBanner} onClick={handleSync}>
          <span style={sx.syncIcon}><FaCloudUploadAlt /></span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <strong>{offlineCount} offline {offlineCount === 1 ? "order" : "orders"} waiting</strong>
            <small style={sx.syncSubtext}>Sync now to include them in reports and cloud backup.</small>
          </span>
          <span style={sx.syncCta}>Sync now <FaArrowRight size={11} /></span>
        </button>
      ) : null}

      <button
        className={`dashboard-insights-toggle${insightsOpen ? " is-open" : " is-closed"}`}
        style={sx.insightsToggle}
        onClick={toggleInsights}
        aria-expanded={insightsOpen}
        aria-controls="owner-business-insights"
      >
        <span style={sx.insightButtonIcon}><FaChartLine /></span>
        <span style={sx.insightsToggleCopy}>
          <strong style={sx.insightsToggleTitle}>
            {insightsOpen ? "Hide business insights" : "Explore business insights"}
          </strong>
        </span>
        <span style={sx.liveBadge}><i className="dashboard-live-dot" /> LIVE</span>
        <span className="dashboard-insights-arrow" style={sx.insightsArrow}>
          {insightsOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </button>

      <div
        id="owner-business-insights"
        className={`dashboard-insights-shell${insightsOpen ? " is-open" : " is-closed"}`}
        aria-hidden={!insightsOpen}
      >
      {/* Stats */}
      <section className="dashboard-owner-stats" style={{ ...sx.statsRow, ...sx.anim.fadeUp(0.04) }}>
        <div style={{ ...sx.statCard, ...sx.anim.riseIn(0.05) }}>
          <div style={sx.statTopRow}>
            <div style={sx.metricIcon}><FaRupeeSign /></div>
            <div style={sx.todayTag}>{isToday ? "TODAY" : "SELECTED DAY"}</div>
          </div>
          <div style={sx.statTitle}>Net sales</div>
          <div style={sx.statValue}>{formatINR(totals.amount)}</div>
          <div style={sx.statHint}>Across {totals.count} completed orders</div>
        </div>
        <div style={{ ...sx.statCard, ...sx.anim.riseIn(0.1) }}>
          <div style={sx.statTopRow}>
            <div style={{ ...sx.metricIcon, background: "#ecfdf5", color: "#059669" }}><FaReceipt /></div>
          </div>
          <div style={sx.statTitle}>Total orders</div>
          <div style={sx.statValue}>{totals.count}</div>
          <div style={sx.statHint}>{insights.dineIn} dine-in · {insights.takeaway} takeaway</div>
        </div>
        <div style={{ ...sx.statCard, ...sx.anim.riseIn(0.15) }}>
          <div style={sx.statTopRow}>
            <div style={{ ...sx.metricIcon, background: "#fff7ed", color: "#ea580c" }}><FaShoppingBag /></div>
          </div>
          <div style={sx.statTitle}>Average bill</div>
          <div style={sx.statValue}>{formatINR(totals.average)}</div>
          <div style={sx.statHint}>Average value per order</div>
        </div>
        <div style={{ ...sx.statCard, ...sx.anim.riseIn(0.2) }}>
          <div style={sx.statTopRow}>
            <div style={{ ...sx.metricIcon, background: "#f5f3ff", color: "#7c3aed" }}><FaClock /></div>
          </div>
          <div style={sx.statTitle}>Peak hour</div>
          <div style={{ ...sx.statValue, fontSize: 21 }}>
            {insights.peak ? hourLabel(insights.peak.hour) : "—"}
          </div>
          <div style={sx.statHint}>{insights.peak ? `${insights.peak.count} orders in this hour` : "No order activity yet"}</div>
        </div>
        <button
          className="dashboard-active-table-card"
          style={{ ...sx.statCard, ...sx.clickableStatCard, ...sx.anim.riseIn(0.25) }}
          onClick={() => navigate("/tables")}
          aria-label={`Open tables screen. ${activeTableCount} tables active`}
        >
          <div style={sx.statTopRow}>
            <div style={{ ...sx.metricIcon, background: "#ecfeff", color: "#0891b2" }}><FaUtensils /></div>
            <span style={sx.openLabel}>OPEN TABLES <FaArrowRight size={9} /></span>
          </div>
          <div style={sx.statTitle}>Active tables</div>
          <div style={sx.statValue}>{activeTableCount}</div>
          <div style={sx.statHint}>Out of {tableList.length} tables · Click to manage</div>
        </button>
      </section>
      <section className="dashboard-insight-grid" style={sx.insightGrid}>
        <div style={sx.panel}>
          <div style={sx.panelHeader}>
            <div>
              <div style={sx.panelKicker}>SALES RHYTHM</div>
              <h3 style={sx.panelTitle}>Revenue by hour</h3>
            </div>
            <button style={sx.textButton} onClick={() => navigate("/reports/hourly-sales")}>Full report <FaArrowRight size={10} /></button>
          </div>
          <div style={sx.chart}>
            {insights.hourly.map((entry, hour) => {
              const height = entry.amount ? Math.max(8, (entry.amount / insights.maxHourly) * 100) : 3;
              return (
                <div key={hour} style={sx.barColumn} title={`${hourLabel(hour)}: ${formatINR(entry.amount)} · ${entry.count} orders`}>
                  <div style={sx.barTrack}><div style={{ ...sx.bar, height: `${height}%`, opacity: entry.amount ? 1 : 0.18 }} /></div>
                  <span style={sx.barLabel}>{hour % 3 === 0 ? `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}${hour >= 12 ? "p" : "a"}` : ""}</span>
                </div>
              );
            })}
          </div>
          <div style={sx.chartSummary}>
            <FaChartLine color="#0ea5e9" />
            {insights.peak ? <span><strong>{hourLabel(insights.peak.hour)}</strong> is your busiest window. Plan staffing and prep around it.</span> : <span>Hourly insights appear as orders come in.</span>}
          </div>
        </div>

        <div style={sx.panel}>
          <div style={sx.panelKicker}>{isToday ? "LIVE MOMENTUM" : "DAY SNAPSHOT"}</div>
          <h3 style={sx.panelTitle}>{isToday ? "Last 60 minutes" : "Selected day"}</h3>
          <div style={sx.momentumRevenue}>{formatINR(isToday ? insights.recentRevenue : totals.amount)}</div>
          <div style={sx.momentumLabel}>{isToday ? "Revenue in the last hour" : "Revenue on this day"}</div>
          <div style={sx.momentumStats}>
            <div style={sx.momentumStat}><strong>{isToday ? insights.recentOrders : totals.count}</strong><span>Orders</span></div>
            <div style={sx.momentumStat}><strong>{totals.amount > 0 ? Math.round(((isToday ? insights.recentRevenue : totals.amount) / totals.amount) * 100) : 0}%</strong><span>Of daily sales</span></div>
          </div>
          <div style={sx.momentumTrack}>
            <span style={{ display: "block", height: "100%", borderRadius: 99, background: "linear-gradient(90deg,#14b8a6,#015b9a)", width: `${totals.amount > 0 ? Math.min(100, ((isToday ? insights.recentRevenue : totals.amount) / totals.amount) * 100) : 0}%` }} />
          </div>
          <div style={sx.latestOrder}>
            <FaClock size={12} />
            {insights.latestOrderAt ? `Latest order ${getRelativeTime(insights.latestOrderAt)}` : "No orders received yet"}
          </div>
          <button style={sx.secondaryButton} onClick={() => navigate("/tables")}>Open POS sale</button>
        </div>
      </section>
      </div>

      {/* Orders */}
      <section>
        <div style={{ ...sx.sectionHeader, ...sx.anim.fadeUp(0.1) }}>
          <div><div style={sx.panelKicker}>RECENT ACTIVITY</div><h3 style={sx.sectionTitle}>{isToday ? "Today’s orders" : "Orders for selected day"}</h3></div>
          <button style={sx.textButton} onClick={() => navigate("/sales")}>View all sales <FaArrowRight size={10} /></button>
        </div>

        {status ? <div style={sx.statusBanner}>{status}</div> : null}

        {loading ? (
          <div style={{ ...sx.loaderWrap, ...sx.anim.fadeUp(0.12) }}>
            <div style={sx.spinner} />
            <div style={{ marginTop: 8, opacity: 0.9 }}>Loading orders…</div>
          </div>
        ) : !orders || orders.length === 0 ? (
          <div style={{ ...sx.emptyWrap, ...sx.anim.fadeUp(0.12) }}>
            <div style={sx.emptyBadge}>No orders yet</div>
            <p style={{ opacity: 0.8, marginTop: 6 }}>
              New orders will show up here.
            </p>
          </div>
        ) : (
          <div style={sx.grid}>
            {orders.map((order, index) => {
              const tableInfo = resolveTableLabel(order);
              // Debug: log how table labels are being resolved (remove after verification)
              console.log("Dashboard table label", {
                orderId: order?.id || order?.ordercode,
                tableid: order?.tableid,
                table: order?.table,
                table_type: order?.table_type || order?.tableType || order?.tabletype,
                tableLabel: tableInfo.label,
                tableType: tableInfo.type,
                tableMetaKeys: Object.keys(tableMetaMap || {}).length,
              });
              return (
                <article
                  key={order.id || order.ordercode}
                  style={{ ...sx.card, ...sx.anim.riseIn(0.12 + index * 0.04) }}
                >
                  {/* Header */}
                  <div style={sx.cardHead}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div style={sx.avatar}>
                        <FaReceipt />
                      </div>
                      <div
                        style={{
                          fontWeight: 800,
                          color: "#0f172a",
                          fontSize: 15,
                        }}
                      >
                        #{order?.ordercode || order?.id}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div style={sx.timeChip}>
                        <FaClock size={11} />
                        {getRelativeTime(order?.created_at)}
                      </div>
                      <div style={sx.metaChip}>
                        <FaUtensils size={11} />
                        {tableInfo.label}
                      </div>
                    </div>
                  </div>

                  {/* Created by */}
                  <div style={sx.createdBy}>
                    {order?.added_by_name
                      ? `Created by ${order.added_by_name}`
                      : "Created by Owner"}
                  </div>

                  <div style={sx.hr} />

                  {/* Summary */}
                  <div style={sx.rowBetween}>
                    <div>
                      <div style={sx.metaLabel}>Total Amount</div>
                      <div style={sx.totalValue}>
                        {formatINR(order?.totalamount)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={sx.metaLabel}>Placed on</div>
                      <div style={sx.metaValue}>
                        {formatDate(order?.created_at)}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <button
                    className="dashboard-view-btn"
                    style={sx.viewBtn}
                    onClick={() =>
                      navigate(`/print/${order?.id || order?.orderid || ""}`, {
                        state: { order },
                      })
                    }
                  >
                    View Receipt
                  </button>
                  <div style={sx.actionRow}>
                    <button
                      style={{
                        ...sx.viewBtn,
                        marginTop: 8,
                        background: "#fff",
                        color: BRAND,
                        flex: 1,
                      }}
                      onClick={() => {
                        const ok = window.confirm(
                          "Are you sure you want to edit this order?"
                        );
                        if (!ok) return;
                        navigate(
                          `/order/${encodeURIComponent(
                            order?.tableid || order?.table || "TakeAway-1"
                          )}`,
                          {
                            state: {
                              orderForEdit: order,
                              tableType: tableInfo.type === "takeaway" ? "table" : tableInfo.type,
                              tableLabel: tableInfo.displayName || tableInfo.label,
                            },
                          }
                        );
                      }}
                    >
                      Edit Order
                    </button>
                    {userData?.role === "owner" &&
                    userData?.allowdelete == 1 ? (
                      <button
                        style={{
                          ...sx.viewBtn,
                          marginTop: 8,
                          background: "#fee2e2",
                          color: "#b91c1c",
                          borderColor: "rgba(185,28,28,.35)",
                          flex: 1,
                        }}
                        onClick={() => {
                          const ok = window.confirm(
                            "Are you sure you want to delete this order?"
                          );
                          if (!ok) return;
                          deleteOrderFromDashboard(order);
                        }}
                        disabled={deletingOrderId === order?.id}
                      >
                        {deletingOrderId === order?.id
                          ? "Deleting…"
                          : "Delete"}
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* Toast root */}
      <div id="toast-root" style={sx.toastRoot} />
    </div>
  );
};

/* ------------------------------- styles ------------------------------- */
const sx = {
  page: {
    minHeight: "100vh",
    background: "#f6f8fb",
    color: "#0b1f2a",
    fontFamily:
      '"Sora","Plus Jakarta Sans","Segoe UI",Roboto,Arial,sans-serif',
  },

  hero: {
    margin: "0 16px",
    padding: "26px 28px",
    borderRadius: 22,
    background: "linear-gradient(125deg,#073b61 0%,#015b9a 52%,#087fb5 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    boxShadow: "0 18px 45px rgba(1,91,154,.22)",
  },
  eyebrow: { fontSize: 11, fontWeight: 900, letterSpacing: 1.7, color: "#8ff7d5" },
  heroTitle: { margin: "7px 0 5px", fontSize: "clamp(24px,3vw,34px)", lineHeight: 1.12 },
  heroCopy: { margin: 0, color: "rgba(255,255,255,.75)", fontSize: 13 },
  heroActions: { display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" },
  refreshButton: {
    border: "1px solid rgba(255,255,255,.3)", background: "rgba(255,255,255,.12)",
    color: "#fff", padding: "10px 13px", borderRadius: 10, cursor: "pointer",
    fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 7,
  },
  syncBanner: {
    width: "calc(100% - 32px)", margin: "14px 16px 0", padding: "12px 16px",
    border: "1px solid #fbbf24", background: "#fffbeb", borderRadius: 14,
    color: "#78350f", display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
  },
  syncIcon: { width: 34, height: 34, display: "grid", placeItems: "center", borderRadius: 9, background: "#fef3c7", color: "#d97706" },
  syncSubtext: { display: "block", marginTop: 2, color: "#92400e" },
  syncCta: { display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 900, whiteSpace: "nowrap" },

  welcomeStrip: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "18px 16px 14px",
    padding: "14px 18px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 10px 24px rgba(15,23,42,.06)",
  },
  hello: { fontSize: 13, color: "#334155" },
  restName: { fontSize: 18, fontWeight: 900, color: BRAND, marginTop: 2 },
  welcomeLeft: { display: "flex", flexDirection: "column", gap: 2 },
  syncPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    background: "#0ea5e9",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.6)",
    boxShadow: "0 10px 24px rgba(14,165,233,.3)",
  },

  statsRow: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "18px 16px 22px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },
  statCard: {
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(15,23,42,.08)",
    borderRadius: 18,
    padding: 16,
    boxShadow:
      "0 10px 24px rgba(15,23,42,.07), inset 0 1px 0 rgba(255,255,255,.72)",
    backdropFilter: "blur(8px)",
  },
  clickableStatCard: {
    textAlign: "left", color: "inherit", fontFamily: "inherit", cursor: "pointer",
  },
  openLabel: { color: BRAND, fontSize: 9, fontWeight: 900, display: "inline-flex", alignItems: "center", gap: 5 },
  metricIcon: {
    width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center",
    background: "#e0f2fe", color: BRAND, fontSize: 15,
  },
  statTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  statTitle: { fontSize: 12, fontWeight: 800, color: "#64748b", marginTop: 12 },
  todayTag: {
    background: "linear-gradient(90deg,#e0f2fe,#ecfeff)",
    color: "#0f172a",
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 900,
    color: "#0f172a",
    marginTop: 6,
  },
  statHint: { marginTop: 5, fontSize: 11, color: "#94a3b8", fontWeight: 700 },

  insightsToggle: {
    width: "auto", maxWidth: "calc(100% - 32px)", margin: "18px auto 14px", padding: "8px 9px 8px 10px",
    border: "1px solid rgba(255,255,255,.3)", borderRadius: 999,
    background: "linear-gradient(115deg,#063c64 0%,#015b9a 55%,#0789bd 100%)",
    color: "#fff", fontFamily: "inherit", display: "flex", alignItems: "center",
    justifyContent: "center", gap: 10, cursor: "pointer", textAlign: "center", position: "relative", overflow: "hidden",
    boxShadow: "0 10px 26px rgba(1,91,154,.25),inset 0 1px 0 rgba(255,255,255,.22)",
  },
  insightButtonIcon: { width: 34, height: 34, borderRadius: 99, display: "grid", placeItems: "center", background: "rgba(255,255,255,.14)", color: "#83f8d1", fontSize: 13 },
  insightsToggleCopy: { display: "flex", alignItems: "center" },
  insightsToggleTitle: { fontSize: 13, color: "#fff", whiteSpace: "nowrap" },
  liveBadge: { display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 7px", borderRadius: 99, background: "rgba(5,23,38,.3)", color: "#a7f3d0", fontSize: 8, fontWeight: 900, letterSpacing: .7 },
  insightsArrow: {
    width: 31, height: 31, flex: "0 0 31px", borderRadius: 99, display: "grid",
    placeItems: "center", color: "#063c64", background: "#fff",
    boxShadow: "0 6px 14px rgba(0,0,0,.16)", fontSize: 11,
  },

  insightGrid: {
    margin: "0 16px 22px", display: "grid", gridTemplateColumns: "minmax(0,1.65fr) minmax(260px,.7fr)", gap: 14,
  },
  panel: { background: "#fff", border: "1px solid #e7edf4", borderRadius: 18, padding: 18, boxShadow: "0 10px 28px rgba(15,23,42,.05)" },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 },
  panelKicker: { fontSize: 10, fontWeight: 900, letterSpacing: 1.3, color: "#94a3b8", marginBottom: 4 },
  panelTitle: { margin: 0, color: "#0f172a", fontSize: 17 },
  textButton: { border: 0, background: "transparent", color: BRAND, fontWeight: 900, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
  chart: { height: 170, display: "flex", alignItems: "stretch", gap: 5, marginTop: 18, borderBottom: "1px solid #e2e8f0" },
  barColumn: { flex: 1, minWidth: 4, display: "flex", flexDirection: "column", alignItems: "center" },
  barTrack: { flex: 1, width: "100%", display: "flex", alignItems: "flex-end" },
  bar: { width: "100%", maxWidth: 22, margin: "0 auto", borderRadius: "5px 5px 1px 1px", background: "linear-gradient(180deg,#14b8a6,#015b9a)", transition: "height .35s ease" },
  barLabel: { height: 19, paddingTop: 5, color: "#94a3b8", fontSize: 9, fontWeight: 800 },
  chartSummary: { marginTop: 12, display: "flex", alignItems: "center", gap: 8, color: "#64748b", fontSize: 12 },
  momentumRevenue: { marginTop: 22, fontSize: 30, fontWeight: 900, color: "#0f172a", letterSpacing: "-.5px" },
  momentumLabel: { marginTop: 3, fontSize: 11, fontWeight: 700, color: "#94a3b8" },
  momentumStats: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20 },
  momentumStat: { display: "flex", flexDirection: "column", gap: 3, padding: 10, borderRadius: 10, background: "#f8fafc", color: "#64748b", fontSize: 10 },
  momentumTrack: { height: 7, borderRadius: 99, background: "#e8eef5", overflow: "hidden", margin: "14px 0" },
  latestOrder: { display: "flex", alignItems: "center", gap: 7, color: "#64748b", fontSize: 11, fontWeight: 700, marginBottom: 14 },
  secondaryButton: { width: "100%", border: "1px solid #dbe5ee", background: "#f8fafc", color: BRAND, borderRadius: 10, padding: 9, fontWeight: 900, cursor: "pointer" },

  sectionHeader: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "6px 16px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  sectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  datePickerWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dateInput: {
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 12,
    padding: "7px 12px",
    fontWeight: 700,
    color: "#0f172a",
    background: "rgba(255,255,255,.92)",
    boxShadow: "0 8px 18px rgba(15,23,42,.06)",
  },

  grid: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "0 16px 28px",
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  },
  card: {
    background: "rgba(255,255,255,0.97)",
    border: "1px solid rgba(15,23,42,.08)",
    borderRadius: 18,
    padding: 16,
    boxShadow:
      "0 14px 30px rgba(15,23,42,.08), inset 0 1px 0 rgba(255,255,255,.72)",
    backdropFilter: "blur(6px)",
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: BRAND,
    color: "#fff",
  },
  timeChip: {
    background: "linear-gradient(90deg,#e5f6ff,#ecfeff)",
    border: "1px solid rgba(0,0,0,.06)",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  metaChip: {
    background: "linear-gradient(90deg,#eef2ff,#f5f3ff)",
    color: BRAND,
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  createdBy: { color: "#64748b", fontSize: 12, fontWeight: 600, marginTop: 8 },
  hr: { height: 1, background: "rgba(17,24,39,.08)", margin: "10px 0" },
  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  metaLabel: { color: "#6b7280", fontSize: 12, fontWeight: 800 },
  totalValue: { color: "#0f172a", fontSize: 18, fontWeight: 900, marginTop: 2 },
  metaValue: { color: "#0f172a", fontSize: 13, fontWeight: 800, marginTop: 2 },
  viewBtn: {
    marginTop: 12,
    width: "100%",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(1,91,154,.2)",
    background: "linear-gradient(135deg,#015b9a,#00a0ff)",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  actionRow: {
    display: "flex",
    gap: 10,
    alignItems: "stretch",
  },

  loaderWrap: {
    width: "calc(100% - 32px)",
    margin: "20px 16px",
    display: "grid",
    placeItems: "center",
    color: "#0f172a",
  },
  spinner: {
    width: 28,
    height: 28,
    border: "3px solid #cfe9ff",
    borderTopColor: BRAND,
    borderRadius: "50%",
    animation: "spin .9s linear infinite",
  },

  emptyWrap: {
    width: "calc(100% - 32px)",
    margin: "20px 16px",
    textAlign: "center",
  },
  statusBanner: {
    width: "calc(100% - 32px)",
    margin: "0 16px 12px",
    padding: "10px 14px",
    background: "#e0f2fe",
    border: "1px solid #bfdbfe",
    borderRadius: 12,
    color: "#0f172a",
    fontWeight: 700,
  },
  emptyBadge: {
    display: "inline-block",
    padding: "8px 12px",
    background: "#fff",
    border: "1px solid rgba(17,24,39,.06)",
    borderRadius: 999,
    fontWeight: 800,
  },
  anim: {
    fadeUp: (delay = 0) => ({
      animation: `fadeUp .7s cubic-bezier(.2,.7,.2,1) ${delay}s both`,
      willChange: "transform, opacity",
    }),
    riseIn: (delay = 0) => ({
      animation: `riseIn .8s cubic-bezier(.16,.7,.2,1) ${delay}s both`,
      willChange: "transform, opacity",
    }),
  },

  toastRoot: {
    position: "fixed",
    right: 16,
    bottom: 16,
    display: "grid",
    gap: 8,
    zIndex: 100,
  },
};

/* ----------------------------- lightweight toast ----------------------------- */
function toast(msg, type = "info") {
  const root = document.getElementById("toast-root");
  if (!root) {
    // fallback
    alert(msg);
    return;
  }
  const el = document.createElement("div");
  el.style.padding = "10px 14px";
  el.style.borderRadius = "10px";
  el.style.backdropFilter = "blur(8px)";
  el.style.border = "1px solid rgba(0,0,0,.08)";
  el.style.color = "#0f172a";
  el.style.fontWeight = "700";
  el.style.boxShadow = "0 10px 28px rgba(0,0,0,.12)";
  el.style.background =
    type === "success"
      ? "linear-gradient(90deg,#a8ff78,#78ffd6)"
      : type === "error"
      ? "linear-gradient(90deg,#ffcccb,#ffd1d1)"
      : "linear-gradient(90deg,#e0f3ff,#bff3ff)";
  el.textContent = msg;
  root.appendChild(el);
  setTimeout(() => {
    el.style.transition = "all .4s ease";
    el.style.opacity = "0";
    el.style.transform = "translateY(10px)";
    setTimeout(() => el.remove(), 420);
  }, 2200);
}

/* keyframes (once) */
if (
  typeof document !== "undefined" &&
  !document.getElementById("dashboard-spin-style")
) {
  const styleTag = document.createElement("style");
  styleTag.id = "dashboard-spin-style";
  styleTag.innerHTML = `
    @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
    @keyframes fadeUp{
      0%{opacity:0;transform:translateY(22px)}
      100%{opacity:1;transform:translateY(0)}
    }
    @keyframes riseIn{
      0%{opacity:0;transform:translateY(26px) scale(.95)}
      100%{opacity:1;transform:translateY(0) scale(1)}
    }
    .dashboard-view-btn{
      background-size: 200% 100%;
      background-position: 0% 50%;
      transition: background-position .45s ease, box-shadow .35s ease, transform .2s ease;
    }
    .dashboard-view-btn:hover{
      background-position: 100% 50%;
      box-shadow: 0 14px 30px rgba(1,91,154,.28);
      transform: translateY(-1px);
    }
    .dashboard-active-table-card{transition:transform .2s ease,box-shadow .2s ease,border-color .2s ease}
    .dashboard-active-table-card:hover{transform:translateY(-2px);box-shadow:0 16px 32px rgba(1,91,154,.13)!important;border-color:rgba(1,91,154,.3)!important}
    .dashboard-insights-toggle{transition:transform .25s ease,box-shadow .25s ease,border-color .25s ease}
    .dashboard-insights-toggle>*{position:relative;z-index:2}
    .dashboard-insights-toggle::before{content:"";position:absolute;inset:-80% auto -80% -35%;width:24%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.34),transparent);transform:rotate(18deg);transition:left .65s cubic-bezier(.22,1,.36,1);z-index:1}
    .dashboard-insights-toggle:hover{transform:translateY(-2px) scale(1.015);border-color:rgba(255,255,255,.58)!important;box-shadow:0 15px 32px rgba(1,91,154,.34)!important}
    .dashboard-insights-toggle:hover::before{left:115%}
    .dashboard-insights-toggle:active{transform:translateY(0) scale(.985)}
    .dashboard-live-dot{width:6px;height:6px;border-radius:50%;background:#34d399;box-shadow:0 0 0 0 rgba(52,211,153,.7);animation:dashboardLivePulse 1.8s ease-out infinite}
    .dashboard-insights-arrow{transition:transform .45s cubic-bezier(.22,1,.36,1),box-shadow .25s ease}
    .dashboard-insights-toggle.is-closed .dashboard-insights-arrow{animation:dashboardArrowBounce 1.8s ease-in-out infinite}
    .dashboard-insights-toggle.is-open .dashboard-insights-arrow{transform:rotate(0deg) scale(1.03)}
    .dashboard-insights-shell{
      position:relative;overflow:hidden;transform-origin:top center;
      transition:max-height .82s cubic-bezier(.77,0,.18,1),opacity .32s ease,transform .72s cubic-bezier(.22,1,.36,1),clip-path .78s cubic-bezier(.77,0,.18,1),filter .5s ease;
      will-change:max-height,opacity,transform,clip-path,filter;
    }
    .dashboard-insights-shell::after{
      content:"";position:absolute;inset:0;z-index:20;pointer-events:none;opacity:0;
      background:linear-gradient(180deg,rgba(255,255,255,.58) 0%,rgba(14,165,233,.12) 42%,rgba(1,91,154,.24) 51%,rgba(255,255,255,0) 58%);
      transform:translateY(-115%);
    }
    .dashboard-insights-shell.is-closed{
      max-height:0;opacity:0;transform:translateY(-28px) scaleY(.985);
      clip-path:inset(0 0 100% 0 round 18px);filter:blur(3px);pointer-events:none;
    }
    .dashboard-insights-shell.is-open{
      max-height:2600px;opacity:1;transform:translateY(0) scaleY(1);
      clip-path:inset(0 0 0 0 round 18px);filter:blur(0);
      animation:dashboardCurtainDrop .82s cubic-bezier(.22,1,.36,1) both;
    }
    .dashboard-insights-shell.is-open::after{animation:dashboardCurtainShine .9s cubic-bezier(.22,1,.36,1) both}
    .dashboard-insights-shell.is-open .dashboard-owner-stats{animation:dashboardCurtainContent .7s cubic-bezier(.22,1,.36,1) .12s both!important}
    .dashboard-insights-shell.is-open .dashboard-insight-grid{animation:dashboardCurtainContent .72s cubic-bezier(.22,1,.36,1) .2s both}
    @keyframes dashboardArrowBounce{
      0%,100%{transform:translateY(-2px);box-shadow:0 8px 18px rgba(1,91,154,.22)}
      50%{transform:translateY(5px);box-shadow:0 13px 23px rgba(1,91,154,.34)}
    }
    @keyframes dashboardLivePulse{
      0%{box-shadow:0 0 0 0 rgba(52,211,153,.68)}
      70%{box-shadow:0 0 0 6px rgba(52,211,153,0)}
      100%{box-shadow:0 0 0 0 rgba(52,211,153,0)}
    }
    @keyframes dashboardCurtainDrop{
      0%{box-shadow:0 -12px 28px rgba(1,91,154,.18)}
      55%{box-shadow:0 18px 35px rgba(1,91,154,.14)}
      78%{transform:translateY(3px) scaleY(.998)}
      100%{box-shadow:none;transform:translateY(0) scaleY(1)}
    }
    @keyframes dashboardCurtainShine{
      0%{opacity:0;transform:translateY(-115%)}
      18%{opacity:.8}
      72%{opacity:.42}
      100%{opacity:0;transform:translateY(115%)}
    }
    @keyframes dashboardCurtainContent{
      0%{opacity:0;transform:translateY(-18px)}
      70%{opacity:1;transform:translateY(3px)}
      100%{opacity:1;transform:translateY(0)}
    }
    @media (prefers-reduced-motion:reduce){
      .dashboard-insights-shell,.dashboard-insights-arrow,.dashboard-insights-toggle{animation:none!important;transition:none!important}
    }
    @media (max-width: 900px){
      .dashboard-insight-grid{grid-template-columns:1fr !important}
    }
    @media (max-width: 640px){
      .dashboard-owner-hero{align-items:flex-start !important;flex-direction:column !important;padding:20px !important}
      .dashboard-owner-stats{grid-template-columns:repeat(2,minmax(0,1fr)) !important}
    }
    @media (max-width: 420px){
      .dashboard-owner-stats{grid-template-columns:1fr !important}
    }
  `;
  document.head.appendChild(styleTag);
}

export default Dashboard;
